import type React from "react";
import { Input } from "@planta-v2/pds-ui/input";
import { Label } from "@planta-v2/pds-ui/label";
import type {
  EmailUserCommunicationChannel,
  WhatsAppUserCommunicationChannel,
  LinkedInUserCommunicationChannel,
} from "@shared/schemas/user-communication-channel.schemas";
import { i18n } from "@frontend/i18n";

interface AccountSettingsTabProps {
  readonly emailChannel: EmailUserCommunicationChannel | null;
  readonly whatsappChannel: WhatsAppUserCommunicationChannel | null;
  readonly linkedInChannel: LinkedInUserCommunicationChannel | null;
  readonly verificationSlot?: React.ReactNode;
}

export function AccountSettingsTab({
  emailChannel,
  whatsappChannel,
  linkedInChannel,
  verificationSlot,
}: AccountSettingsTabProps): React.JSX.Element {
  return (
    <div className="flex flex-col">
      {/* Email Field */}
      {emailChannel !== null && (
        <div className="flex flex-col gap-1.5 border-b border-foreground/10 py-3 last:border-b-0">
          <Label
            htmlFor="account-email"
            className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
          >
            {i18n.t("user.account.settings.email")}
          </Label>
          <Input id="account-email" value={emailChannel.identifier} readOnly />
          {emailChannel.isVerified ? (
            <span className="text-xs text-success">
              {i18n.t("user.account.settings.verified")}
            </span>
          ) : (
            <div className="flex flex-col gap-2 text-xs text-muted-foreground">
              <span>{i18n.t("user.account.settings.unverified")}</span>
              {verificationSlot}
            </div>
          )}
        </div>
      )}

      {/* WhatsApp Field */}
      {whatsappChannel !== null && (
        <div className="flex flex-col gap-1.5 border-b border-foreground/10 py-3 last:border-b-0">
          <Label
            htmlFor="account-whatsapp"
            className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
          >
            {i18n.t("user.account.settings.whatsapp")}
          </Label>
          <Input
            id="account-whatsapp"
            value={whatsappChannel.identifier ?? ""}
            readOnly
          />
        </div>
      )}

      {/* LinkedIn Field */}
      {linkedInChannel !== null && (
        <div className="flex flex-col gap-1.5 border-b border-foreground/10 py-3 last:border-b-0">
          <Label
            htmlFor="account-linkedin"
            className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
          >
            {i18n.t("user.account.settings.linkedin")}
          </Label>
          <Input
            id="account-linkedin"
            value={linkedInChannel.identifier}
            readOnly
          />
        </div>
      )}

      {/* Footer */}
      <p className="pt-3 text-pds-2xs text-muted-foreground">
        {i18n.t("user.account.settings.footer")}{" "}
        <a
          href={`mailto:${i18n.t("user.account.settings.footerEmail")}`}
          className="text-foreground underline underline-offset-2"
        >
          {i18n.t("user.account.settings.footerEmail")}
        </a>
      </p>
    </div>
  );
}
